import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

const WelcomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Shuffle function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Original images array
  const originalImages = [
    '/home/photo1.jpeg',
    '/home/photo2.jpeg',
    '/home/photo3.jpeg',
    '/home/photo4.jpeg',
    '/home/photo5.jpeg'
  ];

  // Carousel state with shuffled images
  const [carouselImages] = useState(() => shuffleArray(originalImages));

  // Auto-rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % carouselImages.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] dark:bg-[#1a1a18]">
      {/* Magazine Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAFAF8]/90 dark:bg-[#1a1a18]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="/logo_white.webp" 
            alt="OKIO" 
            className="h-8 w-auto dark:invert-0 invert"
          />
          <button
            onClick={handleGetStarted}
            className="text-sm font-medium text-[#1a1a18] dark:text-[#FAFAF8] hover:opacity-70 transition-opacity"
          >
            {isAuthenticated ? 'Open App' : 'Sign In'}
          </button>
        </div>
      </header>

      {/* Hero - Magazine Cover Style */}
      <section className="relative h-screen min-h-[700px]">
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img 
              src={image} 
              alt="" 
              className="w-full h-full object-cover"
            />
          </div>
        ))}
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-4xl">
            <p className="text-white/80 text-sm tracking-[0.3em] uppercase mb-4 font-light">
              Introducing
            </p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-6 leading-[0.9]">
              Explore Nature.<br />
              <span className="italic">Together.</span>
            </h1>
            <p className="text-white/90 text-lg md:text-xl max-w-xl leading-relaxed font-light">
              Ask questions, make discoveries, and complete projects outdoors—guided by AI that grows with you.
            </p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 right-8 text-white/60 text-xs tracking-widest uppercase writing-mode-vertical hidden md:block">
          <span className="inline-block rotate-90 origin-center translate-x-3">Scroll</span>
        </div>
      </section>

      {/* Editorial Intro */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-8">
            What is OKIO?
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#1a1a18] dark:text-[#FAFAF8] mb-8 leading-tight">
            Your intelligent companion for real-world exploration
          </h2>
          <p className="text-lg text-[#4a4a45] dark:text-[#a0a095] leading-relaxed mb-6">
            Combining conversational AI, multiplayer projects, and location intelligence, OKIO grows with you across every adventure.
          </p>
          <p className="text-base text-[#6a6a65] dark:text-[#808078] leading-relaxed">
            Our first vertical, <em>Okio Nature</em>, focuses on biodiversity exploration—ask questions about any species, complete projects with friends, and track discoveries over time with AI that remembers everything. Whether you're a nature enthusiast, educator, family, or citizen scientist, OKIO makes outdoor exploration accessible, social, and guided by intelligence that adapts to you.
          </p>
        </div>
      </section>

      {/* Magazine Grid - Features as Editorial */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Feature 1: Conversational AI - Large Feature */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div className="aspect-[4/3] lg:aspect-auto lg:row-span-2 bg-[#E8E4DC] dark:bg-[#2a2a25] rounded-sm overflow-hidden relative group">
              <img 
                src="/home/photo1.jpeg" 
                alt="Conversational AI" 
                className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="text-white/60 text-xs tracking-[0.2em] uppercase mb-2">Feature</p>
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-3">Conversational AI</h3>
                <p className="text-white/80 text-sm leading-relaxed max-w-md">
                  Ask any question about the natural world and get instant, contextual answers. Dynamic back-and-forth guidance that adapts to what you're discovering.
                </p>
              </div>
            </div>
            
            <div className="aspect-[4/3] bg-[#D4CFC5] dark:bg-[#353530] rounded-sm overflow-hidden relative group">
              <img 
                src="/home/photo2.jpeg" 
                alt="Multiplayer Projects" 
                className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-white/60 text-xs tracking-[0.2em] uppercase mb-2">Feature</p>
                <h3 className="font-serif text-xl md:text-2xl text-white mb-2">Multiplayer Projects</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Real-time collaborative nature projects. Join solo or team adventures with shared objectives, live chat, and synchronized discoveries.
                </p>
              </div>
            </div>
            
            <div className="aspect-[4/3] bg-[#C9D4C5] dark:bg-[#2d332a] rounded-sm overflow-hidden relative group">
              <img 
                src="/home/photo3.jpeg" 
                alt="Long-Term Memory" 
                className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-white/60 text-xs tracking-[0.2em] uppercase mb-2">Feature</p>
                <h3 className="font-serif text-xl md:text-2xl text-white mb-2">Long-Term Memory</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  AI that remembers every observation across weeks, months, or years. Perfect for garden care, seasonal tracking, and ongoing nature projects.
                </p>
              </div>
            </div>
          </div>

          {/* Feature Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="aspect-square bg-[#E5DDD3] dark:bg-[#2f2d28] rounded-sm overflow-hidden relative group">
              <img 
                src="/home/photo4.jpeg" 
                alt="Smart Action Cards" 
                className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-serif text-lg text-white mb-1">Smart Action Cards</h3>
                <p className="text-white/70 text-xs leading-relaxed">
                  Interactive suggestions and step-by-step field instructions.
                </p>
              </div>
            </div>
            
            <div className="aspect-square bg-[#D5DEDD] dark:bg-[#282d2d] rounded-sm overflow-hidden relative group">
              <img 
                src="/home/photo5.jpeg" 
                alt="Location Intelligence" 
                className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-serif text-lg text-white mb-1">Location Intelligence</h3>
                <p className="text-white/70 text-xs leading-relaxed">
                  GPS tracking, map integration with discovery pins, and smart suggestions.
                </p>
              </div>
            </div>
            
            <div className="aspect-square bg-[#DDD5E0] dark:bg-[#2d282f] rounded-sm overflow-hidden relative group">
              <img 
                src="/home/photo1.jpeg" 
                alt="Community & Sharing" 
                className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-serif text-lg text-white mb-1">Community & Sharing</h3>
                <p className="text-white/70 text-xs leading-relaxed">
                  Explore public discoveries and join community projects.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Section - How It Works */}
      <section className="py-24 md:py-32 px-6 bg-[#F0EDE8] dark:bg-[#222220]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-6 text-center">
            The Journey
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a18] dark:text-[#FAFAF8] mb-16 text-center">
            From curiosity to discovery
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
            {[
              { num: "01", title: "Start a Project", desc: "Choose a solo adventure or create a multiplayer project with friends" },
              { num: "02", title: "Explore Your Surroundings", desc: "Head outdoors and discover the nature around you" },
              { num: "03", title: "Capture & Observe", desc: "Take photos and document your observations with AI assistance" },
              { num: "04", title: "Get AI Insights", desc: "Receive instant species identification and ecological context" },
              { num: "05", title: "Complete Objectives", desc: "Track your progress and discover new species" },
              { num: "06", title: "Share & Contribute", desc: "Add your findings to the global knowledge base" }
            ].map((item, index) => (
              <div key={index} className="relative">
                <span className="font-serif text-5xl text-[#D4CFC5] dark:text-[#3a3a35] absolute -top-2 -left-2">
                  {item.num}
                </span>
                <div className="pt-10">
                  <h3 className="font-medium text-lg text-[#1a1a18] dark:text-[#FAFAF8] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#6a6a65] dark:text-[#909088] text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-bleed Image Break */}
      <section className="relative h-[70vh] min-h-[500px]">
        <img 
          src="/home/photo2.jpeg" 
          alt="" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <blockquote className="text-center px-6 max-w-3xl">
            <p className="font-serif text-2xl md:text-4xl text-white leading-relaxed italic">
              "Unlike other AI assistants, our companion remembers every observation—across weeks, months, or years."
            </p>
          </blockquote>
        </div>
      </section>

      {/* AI Memory Section - Magazine Article Style */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-6">
                Your Biggest Moat
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a18] dark:text-[#FAFAF8] mb-6 leading-tight">
                AI That Remembers
              </h2>
              <p className="text-lg text-[#4a4a45] dark:text-[#a0a095] leading-relaxed mb-6">
                Your garden's memory. Your trail's history. Your discoveries, tracked over time. Perfect for ongoing nature projects that evolve with the seasons.
              </p>
              
              <div className="space-y-6 mt-10">
                <div className="border-l-2 border-[#C4A77D] pl-6">
                  <h4 className="font-medium text-[#1a1a18] dark:text-[#FAFAF8] mb-1">Garden Care Over Months</h4>
                  <p className="text-sm text-[#6a6a65] dark:text-[#909088]">Track plant health, growth patterns, and seasonal changes with AI that learns your garden</p>
                </div>
                <div className="border-l-2 border-[#C4A77D] pl-6">
                  <h4 className="font-medium text-[#1a1a18] dark:text-[#FAFAF8] mb-1">Seasonal Tracking</h4>
                  <p className="text-sm text-[#6a6a65] dark:text-[#909088]">Revisit the same oak tree across seasons—your AI remembers every previous observation</p>
                </div>
                <div className="border-l-2 border-[#C4A77D] pl-6">
                  <h4 className="font-medium text-[#1a1a18] dark:text-[#FAFAF8] mb-1">Proactive Guidance</h4>
                  <p className="text-sm text-[#6a6a65] dark:text-[#909088]">AI suggests next steps based on your history: "Time to check those seedlings you planted 2 weeks ago"</p>
                </div>
                <div className="border-l-2 border-[#C4A77D] pl-6">
                  <h4 className="font-medium text-[#1a1a18] dark:text-[#FAFAF8] mb-1">Pattern Recognition</h4>
                  <p className="text-sm text-[#6a6a65] dark:text-[#909088]">Identifies trends across your observations: "This mushroom appears every October in this exact spot"</p>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-7">
              <div className="bg-[#F5F3EF] dark:bg-[#252523] rounded-sm p-8 md:p-10">
                <p className="text-xs tracking-[0.2em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-8">
                  A conversation over time
                </p>
                
                <div className="space-y-8">
                  <div className="border-b border-[#E0DCD5] dark:border-[#3a3a35] pb-8">
                    <p className="text-xs text-[#8B7355] dark:text-[#C4A77D] mb-2">Week 1 — March 15</p>
                    <p className="text-[#4a4a45] dark:text-[#a0a095] italic">
                      "I planted tomato seedlings in the south bed"
                    </p>
                  </div>
                  
                  <div className="border-b border-[#E0DCD5] dark:border-[#3a3a35] pb-8">
                    <p className="text-xs text-[#8B7355] dark:text-[#C4A77D] mb-2">Week 4 — April 5</p>
                    <p className="text-[#4a4a45] dark:text-[#a0a095] italic mb-3">
                      "How are my tomato seedlings doing? They look yellowing."
                    </p>
                    <p className="text-[#6B8E6B] dark:text-[#8BAF8B] text-sm pl-4 border-l-2 border-[#6B8E6B] dark:border-[#8BAF8B]">
                      AI: "I remember you planted them 3 weeks ago. Yellowing could indicate nitrogen deficiency. Let's check the soil..."
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-[#8B7355] dark:text-[#C4A77D] mb-2">Week 12 — June 1</p>
                    <p className="text-[#4a4a45] dark:text-[#a0a095] italic mb-3">
                      "Checking in on the garden"
                    </p>
                    <p className="text-[#6B8E6B] dark:text-[#8BAF8B] text-sm pl-4 border-l-2 border-[#6B8E6B] dark:border-[#8BAF8B]">
                      AI: "Your tomatoes have recovered well since we addressed the nitrogen issue in April! They should start flowering this week based on the variety you mentioned."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Column Feature - Conversational */}
      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="aspect-[4/5] bg-[#E8E4DC] dark:bg-[#2a2a25] rounded-sm overflow-hidden relative">
              <img 
                src="/home/photo3.jpeg" 
                alt="" 
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="flex flex-col justify-center p-8 lg:p-12">
              <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-6">
                Intelligence On Demand
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a18] dark:text-[#FAFAF8] mb-6 leading-tight">
                Intelligent Guidance, Your Way
              </h2>
              <p className="text-lg text-[#4a4a45] dark:text-[#a0a095] leading-relaxed mb-6">
                Beyond snap-and-ID apps, OKIO adapts to your exploration style. Choose structured projects with clear objectives, or roam freely with AI that guides you along the way.
              </p>
              <p className="text-base text-[#6a6a65] dark:text-[#808078] leading-relaxed mb-8">
                Either way, you get dynamic dialogue, location-based suggestions, and interactive learning—guidance that adapts to your questions and interests in real-time.
              </p>
              
              <div className="space-y-4 text-sm">
                <p className="text-[#4a4a45] dark:text-[#a0a095]">
                  <span className="font-medium text-[#1a1a18] dark:text-[#FAFAF8]">Structured or Freeform</span> — Follow guided projects with objectives, or explore at your own pace
                </p>
                <p className="text-[#4a4a45] dark:text-[#a0a095]">
                  <span className="font-medium text-[#1a1a18] dark:text-[#FAFAF8]">Dynamic Back-and-Forth</span> — Ask anything, drill deeper, like texting an expert naturalist
                </p>
                <p className="text-[#4a4a45] dark:text-[#a0a095]">
                  <span className="font-medium text-[#1a1a18] dark:text-[#FAFAF8]">Location-Aware</span> — AI knows your GPS and suggests what to look for nearby
                </p>
                <p className="text-[#4a4a45] dark:text-[#a0a095]">
                  <span className="font-medium text-[#1a1a18] dark:text-[#FAFAF8]">Interactive Learning</span> — Step-by-step field guides appear in context
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conversation Examples - Magazine Style */}
      <section className="py-20 px-6 bg-[#F5F3EF] dark:bg-[#222220]">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-6 text-center">
            In the field
          </p>
          <h3 className="font-serif text-2xl md:text-3xl text-[#1a1a18] dark:text-[#FAFAF8] mb-12 text-center">
            A conversation in the wild
          </h3>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <span className="text-2xl">📸</span>
              <div className="flex-1 bg-white dark:bg-[#2a2a28] rounded-sm p-5">
                <p className="text-[#4a4a45] dark:text-[#a0a095] italic mb-2">"What kind of mushroom is this?"</p>
                <p className="text-[#6B8E6B] dark:text-[#8BAF8B] text-sm">
                  AI: "That's a Fly Agaric (Amanita muscaria). Highly toxic—don't touch! Want to know where else they grow?"
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <span className="text-2xl">💡</span>
              <div className="flex-1 bg-white dark:bg-[#2a2a28] rounded-sm p-5">
                <p className="text-[#4a4a45] dark:text-[#a0a095] italic mb-2">"What should I look for next?"</p>
                <p className="text-[#6B8E6B] dark:text-[#8BAF8B] text-sm">
                  AI: "Based on this habitat, look for birch trees nearby—you might find birch boletes. Check under fallen logs too."
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <span className="text-2xl">🎓</span>
              <div className="flex-1 bg-white dark:bg-[#2a2a28] rounded-sm p-5">
                <p className="text-[#4a4a45] dark:text-[#a0a095] italic mb-2">"How do I identify boletes safely?"</p>
                <p className="text-[#6B8E6B] dark:text-[#8BAF8B] text-sm">
                  AI: "Here's a learning card: Step 1—Check the underside for pores (not gills). Step 2—Look for the stem's texture..."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Modes Side by Side */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-6 text-center">
            Your Exploration Style
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a18] dark:text-[#FAFAF8] mb-6 text-center">
            Goal-Oriented or Freeform
          </h2>
          <p className="text-lg text-[#4a4a45] dark:text-[#a0a095] leading-relaxed mb-16 text-center max-w-2xl mx-auto">
            Whether you thrive on structure or prefer spontaneous discovery, OKIO adapts to your exploration style. Switch between modes anytime.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Project Mode */}
            <div className="border border-[#E0DCD5] dark:border-[#3a3a35] rounded-sm p-8">
              <div className="w-12 h-12 rounded-full bg-[#E8B87D] flex items-center justify-center mb-6">
                <span className="text-xl">🗺️</span>
              </div>
              <h3 className="font-serif text-2xl text-[#1a1a18] dark:text-[#FAFAF8] mb-3">Project Mode</h3>
              <p className="text-[#6a6a65] dark:text-[#909088] text-sm mb-6">
                Goal-oriented exploration with clear objectives and progress tracking.
              </p>
              
              <ul className="space-y-3 text-sm text-[#4a4a45] dark:text-[#a0a095] mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-[#C4A77D]">✓</span>
                  <span>Structured objectives</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C4A77D]">✓</span>
                  <span>Progress tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C4A77D]">✓</span>
                  <span>Achievements & rewards</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C4A77D]">✓</span>
                  <span>Multiplayer coordination</span>
                </li>
              </ul>
              
              <p className="text-xs text-[#8B7355] dark:text-[#C4A77D] border-t border-[#E0DCD5] dark:border-[#3a3a35] pt-4">
                Example: "Find 5 mushroom species in the forest"
              </p>
            </div>
            
            {/* Explore Mode */}
            <div className="border border-[#E0DCD5] dark:border-[#3a3a35] rounded-sm p-8">
              <div className="w-12 h-12 rounded-full bg-[#7DB8B8] flex items-center justify-center mb-6">
                <span className="text-xl">✨</span>
              </div>
              <h3 className="font-serif text-2xl text-[#1a1a18] dark:text-[#FAFAF8] mb-3">Explore Mode</h3>
              <p className="text-[#6a6a65] dark:text-[#909088] text-sm mb-6">
                Freeform discovery with AI as your passive companion, ready when you need it.
              </p>
              
              <ul className="space-y-3 text-sm text-[#4a4a45] dark:text-[#a0a095] mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-[#7DB8B8]">✓</span>
                  <span>No pressure</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7DB8B8]">✓</span>
                  <span>Ask questions anytime</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7DB8B8]">✓</span>
                  <span>Spontaneous discoveries</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7DB8B8]">✓</span>
                  <span>AI suggests next steps</span>
                </li>
              </ul>
              
              <p className="text-xs text-[#8B7355] dark:text-[#C4A77D] border-t border-[#E0DCD5] dark:border-[#3a3a35] pt-4">
                Example: "What's this tree? Oh cool, what else grows here?"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Full Width Feature - Collective Knowledge */}
      <section className="relative py-24 md:py-32 px-6 bg-[#2C3E50] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/home/photo4.jpeg" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C4A77D] mb-6 text-center">
            Collective Intelligence
          </p>
          <h2 className="font-serif text-3xl md:text-4xl mb-6 text-center">
            Every Discovery Builds Collective Knowledge
          </h2>
          <p className="text-lg text-white/80 leading-relaxed mb-12 text-center max-w-2xl mx-auto">
            Your observations contribute to a living, growing database of biodiversity. Search discoveries by species, location, and season—powered by insights from the entire community.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <h4 className="font-medium mb-2">Cross-User Knowledge</h4>
              <p className="text-sm text-white/60">Observations from multiple users build richer, more accurate species records</p>
            </div>
            <div className="text-center">
              <h4 className="font-medium mb-2">Searchable & Queryable</h4>
              <p className="text-sm text-white/60">Find all mushroom observations in Zürich, or discover what species appear together</p>
            </div>
            <div className="text-center">
              <h4 className="font-medium mb-2">Temporal Tracking</h4>
              <p className="text-sm text-white/60">Observe the same species across seasons and years, tracking changes over time</p>
            </div>
            <div className="text-center">
              <h4 className="font-medium mb-2">AI-Enhanced</h4>
              <p className="text-sm text-white/60">Machine learning analyzes patterns, suggests connections, and improves accuracy</p>
            </div>
          </div>
          
          <div className="mt-16 bg-white/10 backdrop-blur-sm rounded-sm p-6 max-w-2xl mx-auto">
            <p className="text-xs tracking-[0.2em] uppercase text-[#C4A77D] mb-4">Query Examples</p>
            <div className="space-y-3 font-mono text-sm">
              <p className="text-white/80">"Show me all Amanita muscaria in Zürich"</p>
              <p className="text-white/80">"What species are found near oak trees?"</p>
              <p className="text-white/80">"When do chanterelles appear in fall?"</p>
            </div>
          </div>
        </div>
      </section>

      {/* Multiplayer Section */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-6">
                Social Exploration
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a18] dark:text-[#FAFAF8] mb-6 leading-tight">
                Nature is Better Together
              </h2>
              <p className="text-lg text-[#4a4a45] dark:text-[#a0a095] leading-relaxed mb-6">
                Every project supports multiplayer out of the box. Go solo when you want peace, team up with friends for shared discoveries, or compete in real-time challenges.
              </p>
              <p className="text-base text-[#6a6a65] dark:text-[#808078] leading-relaxed mb-8">
                Real-time collaboration meets flexible participation—join projects live or contribute asynchronously across time zones.
              </p>
              
              <div className="space-y-4 text-sm">
                <p className="text-[#4a4a45] dark:text-[#a0a095]">
                  <span className="font-medium text-[#1a1a18] dark:text-[#FAFAF8]">Real-Time Co-op</span> — Coordinate with friends via live chat, shared objectives, and synchronized discoveries
                </p>
                <p className="text-[#4a4a45] dark:text-[#a0a095]">
                  <span className="font-medium text-[#1a1a18] dark:text-[#FAFAF8]">Competitive Challenges</span> — Race against the clock or each other—sudden death mushroom hunts!
                </p>
                <p className="text-[#4a4a45] dark:text-[#a0a095]">
                  <span className="font-medium text-[#1a1a18] dark:text-[#FAFAF8]">Flexible Participation</span> — Projects work across time zones and schedules
                </p>
              </div>
            </div>
            
            <div className="bg-[#F5F3EF] dark:bg-[#252523] rounded-sm p-8">
              <div className="space-y-4">
                <div className="bg-white dark:bg-[#2a2a28] rounded-sm p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[#6B8E6B] flex items-center justify-center text-white text-sm font-medium">J</div>
                    <div>
                      <p className="text-sm font-medium text-[#1a1a18] dark:text-[#FAFAF8]">Jane</p>
                      <p className="text-xs text-[#8B7355] dark:text-[#C4A77D]">2 min ago</p>
                    </div>
                  </div>
                  <p className="text-sm text-[#4a4a45] dark:text-[#a0a095]">"Found a huge oak tree at the north trail! 📍"</p>
                  <p className="text-xs text-[#6B8E6B] dark:text-[#8BAF8B] mt-2">✓ Objective 1/5 complete</p>
                </div>
                
                <div className="bg-white dark:bg-[#2a2a28] rounded-sm p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[#8B6B8E] flex items-center justify-center text-white text-sm font-medium">M</div>
                    <div>
                      <p className="text-sm font-medium text-[#1a1a18] dark:text-[#FAFAF8]">Mike</p>
                      <p className="text-xs text-[#8B7355] dark:text-[#C4A77D]">Just now</p>
                    </div>
                  </div>
                  <p className="text-sm text-[#4a4a45] dark:text-[#a0a095]">"Nice! I'll check near the creek. Meet at the lookout?"</p>
                </div>
                
                <div className="bg-[#6B8E6B] rounded-sm p-4 text-white">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Project Progress</span>
                    <span className="text-xs opacity-80">Team</span>
                  </div>
                  <p className="text-2xl font-serif">3/5</p>
                  <p className="text-xs opacity-80">discoveries</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 md:py-32 px-6 bg-[#F0EDE8] dark:bg-[#222220]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-6 text-center">
            Perfect For
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a18] dark:text-[#FAFAF8] mb-16 text-center">
            Whether you're learning, teaching, or exploring
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { emoji: "❤️", title: "Nature Enthusiasts", desc: "Weekend mushroom hunting, birding expeditions, and exploring local biodiversity at your own pace." },
              { emoji: "🎓", title: "Educators", desc: "Interactive field trips, biodiversity lessons, and hands-on learning experiences for students of all ages." },
              { emoji: "👨‍👩‍👧", title: "Families", desc: "Learning adventures together, discovering nature as a family with AI-guided exploration and fun challenges." },
              { emoji: "🔬", title: "Citizen Scientists", desc: "Contributing to ecological knowledge, documenting local wildlife, and participating in scientific research." }
            ].map((item, index) => (
              <div key={index} className="flex gap-5">
                <span className="text-3xl">{item.emoji}</span>
                <div>
                  <h3 className="font-medium text-lg text-[#1a1a18] dark:text-[#FAFAF8] mb-2">{item.title}</h3>
                  <p className="text-[#6a6a65] dark:text-[#909088] text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Special */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-6">
            What Makes Us Different
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a18] dark:text-[#FAFAF8] mb-16">
            Why OKIO is Special
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-left">
            <div>
              <h3 className="font-medium text-lg text-[#1a1a18] dark:text-[#FAFAF8] mb-2">Flexible Participation</h3>
              <p className="text-[#6a6a65] dark:text-[#909088] text-sm leading-relaxed">
                Real-time AND async multiplayer means you can participate anytime, whether solo or with others across time zones.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-lg text-[#1a1a18] dark:text-[#FAFAF8] mb-2">AI That Grows With You</h3>
              <p className="text-[#6a6a65] dark:text-[#909088] text-sm leading-relaxed">
                Your personal AI companion learns from every observation you make, remembers your history, and proactively suggests what to explore next.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-lg text-[#1a1a18] dark:text-[#FAFAF8] mb-2">Mobile-First</h3>
              <p className="text-[#6a6a65] dark:text-[#909088] text-sm leading-relaxed">
                Designed for the field with offline support, GPS tracking, and optimized mobile experience.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-lg text-[#1a1a18] dark:text-[#FAFAF8] mb-2">Collective Knowledge</h3>
              <p className="text-[#6a6a65] dark:text-[#909088] text-sm leading-relaxed">
                Cross-user knowledge building creates a living database of real-world observations and discoveries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Project Examples - Magazine Grid */}
      <section className="py-24 px-6 bg-[#F5F3EF] dark:bg-[#222220]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8B7355] dark:text-[#C4A77D] mb-6 text-center">
            Real Adventures
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a18] dark:text-[#FAFAF8] mb-16 text-center">
            Projects you can start today
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-[#2a2a28] rounded-sm p-6 flex gap-4">
              <span className="text-4xl">🍄</span>
              <div>
                <h3 className="font-medium text-[#1a1a18] dark:text-[#FAFAF8] mb-1">Mushroom Hunt</h3>
                <p className="text-sm text-[#6a6a65] dark:text-[#909088] mb-3">Find and identify 5 different mushroom species in your local forest</p>
                <p className="text-xs text-[#8B7355] dark:text-[#C4A77D]">2-3 hours · 1-4 players</p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-[#2a2a28] rounded-sm p-6 flex gap-4">
              <span className="text-4xl">⚡</span>
              <div>
                <h3 className="font-medium text-[#1a1a18] dark:text-[#FAFAF8] mb-1">Speed ID Challenge</h3>
                <p className="text-sm text-[#6a6a65] dark:text-[#909088] mb-3">Compete with friends—who can identify 10 species fastest?</p>
                <p className="text-xs text-[#8B7355] dark:text-[#C4A77D]">30 min · Competitive</p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-[#2a2a28] rounded-sm p-6 flex gap-4">
              <span className="text-4xl">🦜</span>
              <div>
                <h3 className="font-medium text-[#1a1a18] dark:text-[#FAFAF8] mb-1">Bird Migration Watch</h3>
                <p className="text-sm text-[#6a6a65] dark:text-[#909088] mb-3">Document migratory birds across weeks and contribute to science</p>
                <p className="text-xs text-[#8B7355] dark:text-[#C4A77D]">1-2 weeks · Any location</p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-[#2a2a28] rounded-sm p-6 flex gap-4">
              <span className="text-4xl">🌱</span>
              <div>
                <h3 className="font-medium text-[#1a1a18] dark:text-[#FAFAF8] mb-1">30-Day Garden Care</h3>
                <p className="text-sm text-[#6a6a65] dark:text-[#909088] mb-3">Track plant health over a month with AI that remembers every check-in</p>
                <p className="text-xs text-[#8B7355] dark:text-[#C4A77D]">30 days · Long-term</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Magazine Style */}
      <section className="relative py-32 md:py-40 px-6">
        <div className="absolute inset-0">
          <img 
            src="/home/photo5.jpeg" 
            alt="" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        
        <div className="relative max-w-3xl mx-auto text-center text-white">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight">
            Ready to Start Exploring?
          </h2>
          <p className="text-lg md:text-xl mb-12 text-white/80 max-w-xl mx-auto">
            Join alpha users discovering the natural world through AI-guided collaborative exploration. Your next adventure is just a project away.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleGetStarted}
              className="px-10 py-4 bg-white text-[#1a1a18] hover:bg-white/90 font-medium rounded-sm transition-colors text-base min-w-[200px]"
            >
              {isAuthenticated ? 'Start a Project' : 'Create Account'}
              <ArrowRightIcon className="inline-block ml-2 h-4 w-4" />
            </button>
            <button
              onClick={() => navigate('/starters')}
              className="px-10 py-4 bg-transparent border border-white/50 hover:bg-white/10 text-white font-medium rounded-sm transition-colors text-base min-w-[200px]"
            >
              Explore Projects
            </button>
          </div>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="py-12 px-6 bg-[#1a1a18] text-white/60">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <img 
            src="/logo_white.webp" 
            alt="OKIO" 
            className="h-6 w-auto opacity-60"
          />
          <p className="text-sm">
            Questions? Visit our{' '}
            <button onClick={() => navigate('/discover')} className="text-white/80 hover:text-white underline">
              discover page
            </button>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WelcomePage;
